import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NotificationCenter from './NotificationCenter';
import { useAuth } from '../contexts/AuthContext';
import { siteConfig } from '../config/siteConfig';
import ffpLogo from '../assets/images/logo/ffp-logo.svg';

// Error Boundary for NotificationCenter
class NotificationErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {

  }

  render() {
    if (this.state.hasError) {
      // Fallback UI - simple bell icon without functionality
      return (
        <div className="relative p-2" title="Notifications temporarily unavailable">
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.868 19.718A10.951 10.951 0 0112 21c6.075 0 11-4.925 11-11S18.075 0 12 0 1 4.925 1 11a10.951 10.951 0 007.718 10.468" />
          </svg>
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-gray-300 rounded-full" title="Offline" />
        </div>
      );
    }

    return this.props.children;
  }
}

function Header({ onLaunchWizard }) {
  const navigate = useNavigate();
  const { user, token, logout, isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Function to navigate to login page and scroll to form
  const handleLoginClick = () => {
    navigate('/login');
    // Small delay to ensure page has loaded before scrolling
    setTimeout(() => {
      const formElement = document.querySelector('.ffp-login-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    closeMobileMenu();
  };

  // Function to navigate to forgot password page and scroll to form
  const handleForgotPasswordClick = () => {
    navigate('/forgot-password');
    // Small delay to ensure page has loaded before scrolling
    setTimeout(() => {
      const formElement = document.querySelector('.ffp-login-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    closeMobileMenu();
  };

  // Function to navigate to admin login page and scroll to form
  const handleAdminLoginClick = () => {
    navigate('/admin-login');
    // Small delay to ensure page has loaded before scrolling
    setTimeout(() => {
      const formElement = document.querySelector('.ffp-login-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    closeMobileMenu();
  };

  return (
    <header className="text-white py-3 shadow-lg sticky top-0 z-50 bg-brand-navy">
      <div className="ffp-container-reading flex justify-between items-center">
        {/* Logo Section - Compact */}
        <div className="flex items-center">
          <Link
            to="/"
            className="flex items-center hover:opacity-90 transition-opacity"
          >
            <img
              src={ffpLogo}
              alt="Flood and Fire Pros"
              style={{ height: 56, width: 'auto' }}
            />
          </Link>
          {/* Tagline for Inland Empire */}
          <div className="ml-3 hidden lg:block">
            <span className="font-open-sans text-sm text-gray-300">
              Inland Empire Restoration Network
            </span>
          </div>
        </div>

        {/* Unified Navigation - Auth Dependent */}
        <div className="flex items-center space-x-4">

          {/* Authenticated User Navigation */}
          {isAuthenticated ? (
            <>
              <NotificationErrorBoundary>
                <NotificationCenter token={token} user={user} />
              </NotificationErrorBoundary>
              <div className="text-white text-sm font-medium">
                {user?.firstName} {user?.lastName}
              </div>
              <button
                onClick={logout}
                className="text-white transition-colors font-montserrat font-medium text-sm min-h-[44px] px-3 flex items-center"
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={handleLoginClick}
              className="text-white transition-colors font-montserrat font-medium text-sm min-h-[44px] px-3 flex items-center"
            >
              Login
            </button>
          )}

          <button
            onClick={toggleMobileMenu}
            data-testid="mobile-menu-toggle"
            className="text-white transition-colors z-50 relative p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Mobile Backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={closeMobileMenu}
          ></div>

          {/* Mobile Menu */}
          <nav
            data-testid="mobile-menu"
            className="md:hidden fixed top-0 right-0 w-full max-w-md h-full z-[70] overflow-y-auto shadow-xl flex flex-col bg-white"
          >
            {/* Mobile Menu Header */}
            <div className="p-4 flex justify-between items-center border-b-2" style={{ backgroundColor: siteConfig.colors.secondary, borderBottomColor: siteConfig.colors.primary }}>
              <img
                src={ffpLogo}
                alt="Flood and Fire Pros"
                style={{ height: 40, width: 'auto' }}
              />
              <button
                onClick={closeMobileMenu}
                data-testid="mobile-menu-close"
                className="text-white text-3xl min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors"
                aria-label="Close navigation menu"
              >
                ×
              </button>
            </div>

            {/* Mobile Menu Content */}
            <div className="bg-white flex-1 p-4 overflow-y-auto">
              {/* For Homeowners */}
              <div className="mb-6">
                <h3 className="text-lg border-b-2 pb-2 mb-3 font-montserrat font-semibold" style={{ color: siteConfig.colors.secondary, borderBottomColor: siteConfig.colors.primary }}>
                  For Homeowners
                </h3>
                <Link to="/request-service" className="block py-2 text-brand-orange font-semibold border-b border-gray-200 transition-colors font-open-sans" onClick={closeMobileMenu}>
                  Get Help Now
                </Link>
                <Link to="/how-it-works" className="block py-2 text-gray-700 border-b border-gray-200 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>How It Works</Link>
                <Link to="/find-contractors" className="block py-2 text-gray-700 border-b border-gray-200 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Find Contractors</Link>
                <Link to="/signup-homeowner" className="block py-2 text-gray-700 border-b border-gray-200 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Create Account</Link>
                <Link to="/resources" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Resources</Link>
              </div>

              {/* For Contractors */}
              <div className="mb-6">
                <h3 className="text-lg border-b-2 pb-2 mb-3 font-montserrat font-semibold" style={{ color: siteConfig.colors.secondary, borderBottomColor: siteConfig.colors.primary }}>
                  For Contractors
                </h3>
                <Link to="/restoration-professionals" className="block py-2 text-gray-700 border-b border-gray-200 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Why List With Us</Link>
                <Link to="/signup" className="block py-2 text-brand-orange font-semibold border-b border-gray-200 transition-colors font-open-sans" onClick={closeMobileMenu}>
                  Join Our Network
                </Link>
                <Link to="/dashboard" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Contractor Portal</Link>
              </div>

              {/* Services */}
              <div className="mb-6">
                <h3 className="text-lg border-b-2 pb-2 mb-3 font-montserrat font-semibold" style={{ color: siteConfig.colors.secondary, borderBottomColor: siteConfig.colors.primary }}>
                  Services
                </h3>
                <Link to="/water-damage-restoration" className="block py-2 text-gray-700 border-b border-gray-200 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Water Damage</Link>
                <Link to="/fire-damage-restoration" className="block py-2 text-gray-700 border-b border-gray-200 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Fire Damage</Link>
                <Link to="/mold-remediation" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Mold Remediation</Link>
              </div>

              {/* Company */}
              <div className="mb-6">
                <h3 className="text-lg border-b-2 pb-2 mb-3 font-montserrat font-semibold" style={{ color: siteConfig.colors.secondary, borderBottomColor: siteConfig.colors.primary }}>
                  Company
                </h3>
                <Link to="/about" className="block py-2 text-gray-700 border-b border-gray-200 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>About Us</Link>
                <Link to="/contact" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Contact</Link>
              </div>
            </div>

            {/* Mobile Menu Footer */}
            <div className="p-4 border-t-2" style={{ backgroundColor: siteConfig.colors.secondary, borderTopColor: siteConfig.colors.primary }}>
              <button
                onClick={() => {
                  closeMobileMenu();
                  if (onLaunchWizard) onLaunchWizard();
                }}
                className="block w-full text-white py-2 px-4 text-center rounded-lg font-montserrat font-semibold transition-colors text-sm mb-4 bg-brand-orange"
              >
                🚨 Emergency Help
              </button>
              <p className="text-center text-gray-300 text-sm font-open-sans">
                Inland Empire Restoration Network
              </p>
              <p className="text-center text-gray-400 text-xs mt-1">
                © 2025 Flood & Fire Pros
              </p>
            </div>
          </nav>
        </>
      )}

      {/* Desktop Mega Menu Dropdown - Separate from Mobile */}
      {isMobileMenuOpen && (
        <>
          {/* Desktop Backdrop */}
          <div
            className="hidden md:block fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={closeMobileMenu}
          ></div>

          <div className="hidden md:block absolute top-full left-0 w-full bg-white shadow-2xl z-50 border-t-4" style={{ borderTopColor: siteConfig.colors.primary }}>
          <div className="ffp-container-reading py-8">
            <div className="grid grid-cols-4 gap-8">
              {/* For Homeowners */}
              <div>
                <h3 className="text-lg font-montserrat font-semibold border-b-2 pb-3 mb-4" style={{ color: siteConfig.colors.secondary, borderBottomColor: siteConfig.colors.primary }}>
                  For Homeowners
                </h3>
                <Link to="/request-service" className="block py-2 font-semibold transition-colors text-brand-orange hover:text-orange-600" onClick={closeMobileMenu}>
                  Get Help Now
                </Link>
                <Link to="/how-it-works" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>How It Works</Link>
                <Link to="/find-contractors" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Find Contractors</Link>
                <Link to="/signup-homeowner" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Create Account</Link>
                <Link to="/resources" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Resources</Link>
              </div>

              {/* For Contractors */}
              <div>
                <h3 className="text-lg font-montserrat font-semibold border-b-2 pb-3 mb-4" style={{ color: siteConfig.colors.secondary, borderBottomColor: siteConfig.colors.primary }}>
                  For Contractors
                </h3>
                <Link to="/restoration-professionals" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Why List With Us</Link>
                <Link to="/signup" className="block py-2 font-semibold transition-colors text-brand-orange hover:text-orange-600" onClick={closeMobileMenu}>
                  Join Our Network
                </Link>
                <Link to="/dashboard" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Contractor Portal</Link>
              </div>

              {/* Services */}
              <div>
                <h3 className="text-lg font-montserrat font-semibold border-b-2 pb-3 mb-4" style={{ color: siteConfig.colors.secondary, borderBottomColor: siteConfig.colors.primary }}>
                  Services
                </h3>
                <Link to="/water-damage-restoration" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Water Damage</Link>
                <Link to="/fire-damage-restoration" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Fire Damage</Link>
                <Link to="/mold-remediation" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Mold Remediation</Link>
                <Link to="/smoke-odor-removal" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Smoke & Odor Removal</Link>
              </div>

              {/* Company */}
              <div>
                <h3 className="text-lg font-montserrat font-semibold border-b-2 pb-3 mb-4" style={{ color: siteConfig.colors.secondary, borderBottomColor: siteConfig.colors.primary }}>
                  Company
                </h3>
                <Link to="/about" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>About Us</Link>
                <Link to="/contact" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Contact</Link>
                <Link to="/blog" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>Blog</Link>
                <Link to="/faq" className="block py-2 text-gray-700 transition-colors font-open-sans hover:text-brand-orange" onClick={closeMobileMenu}>FAQ</Link>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="mt-6 pt-6 border-t border-gray-200 flex justify-center gap-4">
              <Link
                to="/request-service"
                onClick={closeMobileMenu}
                className="text-white px-6 py-3 rounded-lg font-montserrat font-semibold transition-colors shadow-lg bg-brand-orange hover:bg-orange-600"
              >
                Get Help Now
              </Link>
              <Link
                to="/signup"
                onClick={closeMobileMenu}
                className="text-white px-6 py-3 rounded-lg font-montserrat font-semibold transition-colors shadow-lg bg-brand-navy hover:bg-blue-900"
              >
                Join Our Network
              </Link>
            </div>
          </div>
        </div>
        </>
      )}
    </header>
  );
}

export default Header;
